import React, { useEffect } from "react";
import s from "./InfoSystemManagement.less";
import Button from "~/shared/ui/Fields/Button";
import { SubmitHandler, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useGetLocationData } from "~/hooks/locationData";
import { Input } from "~/shared/ui/Fields/Input";
import MultiInput from "~/shared/ui/Fields/MultiSelect";
import { getFields } from "./fields";
import { infoSystemQueryManagement } from "./queryManagement";
import { useGetPositionsForSelect } from "~/hooks/useGetPositionsForSelect";
import { OpenModalActions } from "~/types/OpenModalActions";
import { SelectOptions } from "~/types/Select";
import { useGetInformationSystemByIdQuery } from "~/schemas/generated/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { getSchema } from "./fieldsValidity";

type Props = {
  refetch: any;
  closeModal: any;
};

type FormSubmit = {
  name: string;
  positions: SelectOptions[];
};
const InfoSystemManagement = ({ refetch, closeModal }: Props) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useGetLocationData();

  const isAdd = openModal === OpenModalActions.Add;
  const isEdit = openModal === OpenModalActions.Edit;

  const readyToGetInfoSystemById =
    Boolean(isAdd && currentId) || Boolean(isEdit && currentId);

  const { positions, intersectionObserver, searchText } =
    useGetPositionsForSelect();

  const { data } = useGetInformationSystemByIdQuery({
    variables: {
      id: currentId,
    },
    skip: !readyToGetInfoSystemById,
    fetchPolicy: "cache-and-network",
  });

  const currentInfoSystem = data?.PositionGetter?.getInformationSystemById;

  const { handleSubmit, control, errors, setValue } = useForm({
    defaultValues: {
      name: currentInfoSystem?.name || "",
      positions: currentInfoSystem?.Positions || [],
    },
    resolver: yupResolver(getSchema()),
  });

  const fields = getFields({
    errors,
    positions,
    intersectionObserver,
    searchText,
  });

  const { editInfoSystem, addInfoSystem } = infoSystemQueryManagement({
    refetch,
    closeModal,
  });

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const { name, positions } = data;

    const positionsIds = positions.map((position) => position.value);

    const variables = {
      name,
      positions: positionsIds,
    };

    if (isAdd) {
      addInfoSystem({ variables });
    }
    if (isEdit) {
      editInfoSystem({
        variables: {
          ...variables,
          id: currentId,
        },
      });
    }
  };

  useEffect(() => {
    if (readyToGetInfoSystemById && currentInfoSystem) {
      const { name, Positions } = currentInfoSystem;

      const positionsForMultiSelect =
        Positions && Array.isArray(Positions)
          ? Positions.map((position) => {
              if (
                position.Position &&
                position.Position.name &&
                position.Position.id
              ) {
                return {
                  label: position.Position.name,
                  value: position.Position.id,
                };
              }
              return null;
            }).filter(Boolean)
          : [];

      setValue("name", name);
      setValue("positions", positionsForMultiSelect);
    }
  }, [readyToGetInfoSystemById, currentInfoSystem]);

  return (
    <form className={s.form} onSubmit={handleSubmit(submit)}>
      <div className={s.items}>
        {fields.map((field) => (
          <div className={s.item} key={field.name}>
            {field.isInput && (
              <Input
                label={field.label}
                name={field.name}
                control={control}
                customClassName={s.input}
                disabled={field.disabled}
                error={field.error}
              />
            )}
            {field.isMultiSelect && (
              <div className={s.multi_select_wrapper}>
                <MultiInput
                  name={field.name}
                  label={field.label}
                  control={control}
                  options={field.options}
                  isNonSrcoll={false}
                  error={field.error}
                  onScrollBottom={field.scrollBottom}
                  getSearchText={field.searchText}
                  isDisabled={field.disabled}
                />
              </div>
            )}
          </div>
        ))}
        <div className={s.item}>
          <Button txtBtn={t("ui.save")} isForm />
        </div>
      </div>
    </form>
  );
};

export default InfoSystemManagement;
